import { useEffect, useState, type FormEvent } from "react";
import { authService } from "../../services";
import { PageHeader } from "../../components/feature/PageHeader";
import { Card } from "../../components/base/Card";
import { Field } from "../../components/base/Field";
import { Button } from "../../components/base/Button";
import { Avatar } from "../../components/base/Avatar";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { roleLabel } from "../../config/roles";
import { formatLongDate } from "../../utils/format";

export default function ProfilePage() {
  const { user, role, updateUser } = useAuth();
  const toast = useToast();

  const [name, setName] = useState(user?.name ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const [phone, setPhone] = useState(user?.phone ?? "");

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwError, setPwError] = useState<string | null>(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name);
      setEmail(user.email);
      setPhone(user.phone ?? "");
    }
  }, [user]);

  const saveProfile = async (e: FormEvent) => {
    e.preventDefault();
    if (savingProfile) return;
    setSavingProfile(true);
    try {
      const updatedUser = await authService.updateProfile({ name, email, phone });
      updateUser(updatedUser);
      toast.success("Profile updated", "Your details were saved successfully.");
    } catch (err) {
      toast.error("Could not save profile", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (e: FormEvent) => {
    e.preventDefault();
    if (savingPw) return;
    setPwError(null);
    if (!currentPw) {
      setPwError("Current password is required.");
      return;
    }
    if (newPw.length < 8) {
      setPwError("New password must be at least 8 characters.");
      return;
    }
    if (newPw !== confirmPw) {
      setPwError("New password and confirmation do not match.");
      return;
    }
    setSavingPw(true);
    try {
      await authService.changePassword(currentPw, newPw);
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
      toast.success("Password changed", "Your password was updated successfully.");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Could not update password.";
      setPwError(msg);
      toast.error("Could not change password", msg);
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="My Profile"
        subtitle="View and update your account details and password."
        breadcrumb={<span>Account / Profile</span>}
      />

      <div className="mb-4 flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
        <Avatar name={user?.name ?? "User"} color={user?.avatarColor} size="lg" />
        <div>
          <p className="text-sm font-semibold text-foreground-900">{user?.name}</p>
          <p className="text-xs text-foreground-500">
            {roleLabel(role)} · Joined {formatLongDate(new Date())}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card title="Profile information" subtitle="Your basic account details">
          <form onSubmit={saveProfile} className="space-y-4">
            <Field label="Full name" htmlFor="profile-name" required>
              <input
                id="profile-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-9 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40"
              />
            </Field>
            <Field label="Email address" htmlFor="profile-email" required>
              <input
                id="profile-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-9 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40"
              />
            </Field>
            <Field label="Phone number" htmlFor="profile-phone">
              <input
                id="profile-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="h-9 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40"
              />
            </Field>
            <div className="flex justify-end">
              <Button type="submit" loading={savingProfile} icon={<i className="ri-save-line" />}>
                Save changes
              </Button>
            </div>
          </form>
        </Card>

        <Card title="Change password" subtitle="Update your account password">
          <form onSubmit={savePassword} className="space-y-4">
            {pwError ? (
              <div className="flex items-start gap-2 rounded-md border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">
                <i className="ri-error-warning-line mt-0.5 flex-none text-base" />
                <span>{pwError}</span>
              </div>
            ) : null}
            <Field label="Current password" htmlFor="current-pw" required>
              <input
                id="current-pw"
                type="password"
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                className="h-9 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40"
              />
            </Field>
            <Field label="New password" htmlFor="new-pw" required helper="At least 8 characters.">
              <input
                id="new-pw"
                type="password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                className="h-9 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40"
              />
            </Field>
            <Field label="Confirm new password" htmlFor="confirm-pw" required>
              <input
                id="confirm-pw"
                type="password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                className="h-9 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40"
              />
            </Field>
            <div className="flex justify-end">
              <Button type="submit" loading={savingPw} icon={<i className="ri-key-line" />}>
                Update password
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}